"use client";

import { useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

// ─── Shader Material (internal) ───────────────────────────────────────────────

const DotMatrixMaterial = () => {
  const { size } = useThree();
  const ref = useRef(null);

  const material = useMemo(() => {
    const uniforms = {
      u_time: { value: 0 },
      u_resolution: {
        value: new THREE.Vector2(size.width * 2, size.height * 2),
      },
      u_colors: {
        value: [
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
        ],
      },
      u_opacities: {
        value: [0.3, 0.3, 0.3, 0.5, 0.5, 0.5, 0.8, 0.8, 0.8, 1],
      },
      u_total_size: { value: 20 },
      u_dot_size: { value: 6 },   // fixed: 6px
      u_reverse: { value: 0 },
    };

    return new THREE.ShaderMaterial({
      vertexShader: `
        precision mediump float;
        uniform vec2 u_resolution;
        out vec2 fragCoord;
        void main() {
          gl_Position = vec4(position.xy, 0.0, 1.0);
          fragCoord = (position.xy + vec2(1.0)) * 0.5 * u_resolution;
          fragCoord.y = u_resolution.y - fragCoord.y;
        }
      `,
      fragmentShader: `
        precision mediump float;
        in vec2 fragCoord;

        uniform float u_time;
        uniform float u_opacities[10];
        uniform vec3 u_colors[6];
        uniform float u_total_size;
        uniform float u_dot_size;
        uniform vec2 u_resolution;
        uniform int u_reverse;

        out vec4 fragColor;

        float PHI = 1.61803398874989484820459;
        float random(vec2 xy) {
          return fract(tan(distance(xy * PHI, xy) * 0.5) * xy.x);
        }

        void main() {
          vec2 st = fragCoord.xy;
          st.x -= abs(floor((mod(u_resolution.x, u_total_size) - u_dot_size) * 0.5));
          st.y -= abs(floor((mod(u_resolution.y, u_total_size) - u_dot_size) * 0.5));

          float opacity = step(0.0, st.x) * step(0.0, st.y);

          vec2 st2 = vec2(int(st.x / u_total_size), int(st.y / u_total_size));

          float frequency = 5.0;
          float show_offset = random(st2);
          float rand = random(st2 * floor((u_time / frequency) + show_offset + frequency));
          opacity *= u_opacities[int(rand * 10.0)];
          opacity *= 1.0 - step(u_dot_size / u_total_size, fract(st.x / u_total_size));
          opacity *= 1.0 - step(u_dot_size / u_total_size, fract(st.y / u_total_size));

          vec3 color = u_colors[int(show_offset * 6.0)];

          float animation_speed_factor = 0.5;
          vec2 center_grid = u_resolution / 2.0 / u_total_size;
          float dist_from_center = distance(center_grid, st2);
          float timing_offset = dist_from_center * 0.01 + (random(st2) * 0.15);

          opacity *= step(timing_offset, u_time * animation_speed_factor);
          opacity *= clamp((1.0 - step(timing_offset + 0.1, u_time * animation_speed_factor)) * 1.25, 1.0, 1.25);

          fragColor = vec4(color, opacity);
          fragColor.rgb *= fragColor.a;
        }
      `,
      uniforms,
      glslVersion: THREE.GLSL3,
      blending: THREE.CustomBlending,
      blendSrc: THREE.SrcAlphaFactor,
      blendDst: THREE.OneFactor,
    });
  }, [size.width, size.height]);

  useFrame(({ clock }) => {
    if (ref.current) {
      ref.current.material.uniforms.u_time.value = clock.getElapsedTime() * (3.0 / 5.0); // fixed: 3x speed
    }
  });

  return (
    <mesh ref={ref}>
      <planeGeometry args={[2, 2]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
};

// ─── Public wrapper component ──────────────────────────────────────────────────

/**
 * HeroBackground
 *
 * Wrap your hero section content with this component.
 * It renders the white dot-matrix WebGL animation as the background.
 *
 * Usage:
 *   <HeroBackground>
 *     <h1>Your hero content</h1>
 *   </HeroBackground>
 *
 * Props:
 *   children      — your hero content (rendered on top of the animation)
 *   className     — optional extra classes on the outer container
 *   showVignette  — adds a radial dark vignette over the animation (default: true)
 */
export default function HeroBackground({
  children,
  className = "",
  showVignette = true,
}) {
  return (
    <div
      className={`relative w-full overflow-hidden bg-black ${className}`}
      style={{ isolation: "isolate" }}
    >
      {/* WebGL dot-matrix layer */}
      <div className="absolute inset-0 z-0">
        <Canvas
          className="absolute inset-0 h-full w-full"
          style={{ display: "block" }}
        >
          <DotMatrixMaterial />
        </Canvas>

        {/* Bottom fade so content below the hero doesn't clash */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to top, black 0%, transparent 40%)",
          }}
        />

        {/* Optional radial vignette */}
        {showVignette && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.9) 100%)",
            }}
          />
        )}
      </div>

      {/* Hero content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}